import React, { useEffect, useState } from 'react';
import { historyService } from '../services/historyService';
import { logUiEvent } from '../services/uiLogger';
import { viewsLabel } from '../lib/shareViews';

const TTL_OPTIONS = [
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: 'forever', label: 'Бессрочно' },
];

/**
 * Текст письма со ссылкой — менеджер отправляет его клиенту после встречи, не
 * сочиняя сопровождение сам (дизайн-спринт 23.09, п. 2.9). Слово подбирается по
 * тому, что реально уйдёт по ссылке.
 */
export function shareLetter(link, includes) {
  const what = includes.includes('protocol')
    ? (includes.includes('transcript') ? 'протокол и расшифровка' : 'протокол')
    : 'расшифровка';
  return `Добрый день!\n\nВот ${what} нашей встречи: ${link}\n\n`
    + 'Ссылка открывается без регистрации.';
}

/**
 * Модалка «Поделиться» для записи Истории.
 *
 * props:
 *  - itemId: id записи
 *  - title: название записи — чтобы было видно, чем именно делишься (необязательно)
 *  - available: ['transcript','protocol'] — что есть в записи
 *  - protocolLabel: имя протокола, который уедет по ссылке (когда их несколько)
 *  - share: текущее состояние ссылки { token, includes, expires_at, url, views, last_view_at } | null
 *  - onClose(): закрыть
 *  - onChanged(share|null): ссылка создана/обновлена (share) или отозвана (null)
 */
export default function ShareModal({ itemId, title, available, protocolLabel, share, onClose, onChanged }) {
  const [includes, setIncludes] = useState(
    share?.includes?.length ? share.includes : available
  );
  const [ttl, setTtl] = useState(share?.ttl || '7d');
  const [link, setLink] = useState(share?.url || '');
  // Что лежит по ссылке сейчас — галочки могли поменять, не нажав «Обновить».
  const [linkIncludes, setLinkIncludes] = useState(share?.includes?.length ? share.includes : available);
  // Счётчик открытий: после «Создать/Обновить» — из ответа, до того — из props.
  const [fresh, setFresh] = useState(null);
  const seen = fresh || share;
  // Экран протокола открывает окно сразу, а ссылку записи подтягивает следом
  // (MeetingProtocol.openShare): без этого окно предлагало «Создать ссылку»
  // записи, у которой ссылка уже есть, и не показывало ни её, ни «Отозвать».
  useEffect(() => {
    if (!share?.url) return;
    setLink(share.url);
    setTtl(share.ttl || '7d');
    if (share.includes?.length) {
      setIncludes(share.includes);
      setLinkIncludes(share.includes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [share?.token]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggleInc = (part) => {
    setIncludes((prev) =>
      prev.includes(part) ? prev.filter((p) => p !== part) : [...prev, part]
    );
  };

  const createOrUpdate = async () => {
    if (!includes.length) return;
    setBusy(true);
    try {
      const res = await historyService.createShare(itemId, { includes, ttl });
      setLink(res.url);
      setLinkIncludes(res.includes?.length ? res.includes : includes);
      setFresh(res);
      onChanged?.(res);
      logUiEvent('share_create', { component: 'history', meta: { includes, ttl } });
      // Ссылка создаётся, чтобы её отправить, — кладём в буфер сразу (обращение 25d95a66).
      // Браузер может отказать после await (нет жеста) — тогда остаётся кнопка «Копировать».
      try {
        await navigator.clipboard.writeText(res.url);
        setCopied('created');
        setTimeout(() => setCopied(false), 3000);
      } catch (_) {}
    } catch (_) { /* без ломки UX */ } finally { setBusy(false); }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await historyService.revokeShare(itemId);
      setLink('');
      onChanged?.(null);
      logUiEvent('share_revoke', { component: 'history' });
    } catch (_) {} finally { setBusy(false); }
  };

  // copied — какая из двух кнопок сработала: 'link' или 'letter'.
  const copy = async (kind) => {
    const text = kind === 'letter' ? shareLetter(link, linkIncludes) : link;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(false), 1500);
      if (kind === 'letter') logUiEvent('share_copy_letter', { component: 'history' });
    } catch (_) {}
  };

  // У записи может быть несколько протоколов, а по ссылке уйдёт один — открытый.
  // Без имени человек не знает, какой именно, и рискует отправить наружу не тот.
  const labelFor = (p) => {
    if (p === 'transcript') return 'Транскрибация';
    return protocolLabel ? `Протокол: ${protocolLabel}` : 'Протокол';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-paper-card rounded-2xl shadow-xl w-full max-w-md mx-4 max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h3 className="text-base font-semibold">Поделиться</h3>
          <button onClick={onClose} className="text-paper-hint hover:text-paper-ink-2">✕</button>
        </div>
        {title && (
          // Заголовок модалки один на все записи — без имени не видно, чем делишься.
          // Длинное имя обрезаем, целиком показываем по наведению.
          <div data-testid="share-title" title={title}
            className="px-5 pb-1 text-sm text-paper-ink-3 truncate">
            {title}
          </div>
        )}
        <div className="px-5 pb-5">
          <div className="text-[11px] uppercase tracking-wide text-paper-hint mt-2 mb-2">Что включить в ссылку</div>
          {available.map((p) => (
            <label key={p}
              className={`flex items-center gap-3 p-2.5 mb-2 rounded-panel cursor-pointer ${includes.includes(p) ? '' : ''}`}
              >
              <input type="checkbox" checked={includes.includes(p)} onChange={() => toggleInc(p)} />
              <span className="text-sm font-medium text-paper-ink-2">{labelFor(p)}</span>
            </label>
          ))}
          {protocolLabel && (
            <div className="text-xs text-paper-ink-3 -mt-1 mb-1">
              По ссылке уйдёт только открытый протокол. Другой — откройте его и обновите ссылку.
            </div>
          )}

          <div className="text-[11px] uppercase tracking-wide text-paper-hint mt-3 mb-2">Срок действия ссылки</div>
          <div className="flex gap-2">
            {TTL_OPTIONS.map((o) => (
              <button key={o.value} onClick={() => setTtl(o.value)}
                className={`flex-1 py-2 rounded-panel text-sm font-semibold border ${ttl === o.value ? 'bg-paper-accent text-white ' : 'text-paper-ink-2 '}`}>
                {o.label}
              </button>
            ))}
          </div>

          {link ? (
            <>
              <div className="text-[11px] uppercase tracking-wide text-paper-hint mt-4 mb-2">Ссылка</div>
              {copied === 'created' && (
                <div data-testid="share-auto-copied" className="text-xs text-paper-accent mb-2">Ссылка скопирована в буфер обмена</div>
              )}
              <div className="flex gap-2">
                <input readOnly value={link} className="flex-1 text-sm px-3 py-2 rounded-panel bg-paper-card bg-paper-panel" />
                <button onClick={() => copy('link')} className="px-4 rounded-panel text-sm font-bold text-white bg-paper-accent">{copied === 'link' ? 'Готово' : 'Копировать'}</button>
              </div>
              <div data-testid="share-views" className="mt-2 flex items-center gap-2 text-[13px]">
                <span className={`h-2 w-2 shrink-0 rounded-full ${seen?.views ? 'bg-paper-accent' : 'bg-paper-track'}`} />
                <span className={seen?.views ? 'text-paper-ink' : 'text-paper-ink-3'}>
                  {viewsLabel(seen?.views, seen?.last_view_at)}
                </span>
              </div>
              <button data-testid="share-copy-letter" onClick={() => copy('letter')}
                className="mt-2 w-full py-2 rounded-panel text-sm font-semibold border text-paper-accent">
                {copied === 'letter' ? 'Письмо скопировано' : 'Скопировать с письмом'}
              </button>
              <div className="flex items-start gap-2 mt-3 text-xs text-paper-ink-3 bg-paper-panel rounded-panel p-2.5">
                <span>🔓</span>
                <span>Откроется у любого, кто получит ссылку — без входа в Бит.Ньютон. Доступ можно отозвать в любой момент.</span>
              </div>
              <div className="flex gap-2 mt-3">
                <button disabled={busy} onClick={createOrUpdate} className="flex-1 py-2 rounded-panel text-sm font-semibold border text-paper-accent">Обновить ссылку</button>
                <button disabled={busy} onClick={revoke} className="flex-1 py-2 rounded-panel text-sm font-semibold text-paper-accent border ">Отозвать</button>
              </div>
            </>
          ) : (
            <button disabled={busy || !includes.length} onClick={createOrUpdate}
              className="mt-4 w-full py-2.5 rounded-panel text-sm font-bold text-white bg-paper-accent disabled:opacity-50">
              Создать ссылку
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
